import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

import * as Sentry from '@sentry/angular';

// Fallback DSN points to the EU data-residency zone (workheld-gmbh org, ingest.de)
const sentryDsn =
  process.env['SENTRY_DSN'] ||
  'https://d63a3d3149129852198da208bd43d0bd@o4509479121715200.ingest.de.sentry.io/4511033452724304';
const sentryRelease =
  process.env['SENTRY_RELEASE'] || `flow-web@${environment.version}`;

const PROD_HOST_ENVIRONMENTS: Record<string, string> = {
  'flow-preprod.workheld.com': 'preprod',
  'flow.workheld.com': 'prod',
  'flow.hcb.workheld.com': 'prod-hcb',
  'flowclient-wefranz-prod.azurewebsites.net': 'prod-wefranz',
};

const sentryEnvironment =
  PROD_HOST_ENVIRONMENTS[window.location.hostname] ||
  (environment.stage === 'prod' ? 'prod' : environment.deploymentEnv);

const isProdEnvironment = sentryEnvironment.startsWith('prod');

Sentry.init({
  environment: sentryEnvironment,
  dsn: sentryDsn,
  release: sentryRelease,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
      unblock: ['.sentry-unblock', '[data-sentry-unblock]'],
      unmask: ['.sentry-unmask', '[data-sentry-unmask]'],
    }),
  ],
  // Tracing - environment-aware sample rates
  tracesSampleRate: isProdEnvironment ? 0.2 : 1,
  // Distributed tracing to backend APIs (workheld-platform / galaxy-api)
  tracePropagationTargets: [
    /^https:\/\/workheld-gw(-dev|-test)?\.azure-api\.net\/?/,
    /^https:\/\/white-pebble.*\..*/,
    /^https:\/\/flow(-(dev|test)|\.lewa)?\.workheld\.com\/?.*/,
    /^https:\/\/flowclient-wefranz-prod\.azurewebsites\.net\/?.*/,
  ],
  // Session Replay - environment-aware sample rates
  replaysSessionSampleRate: isProdEnvironment ? 0.1 : 0.5,
  replaysOnErrorSampleRate: 1,
  // Profiling - environment-aware sample rates
  profilesSampleRate: isProdEnvironment ? 0.2 : 1,
  // Send default PII (disabled for GDPR)
  sendDefaultPii: false,
  // Breadcrumb depth
  maxBreadcrumbs: 100,
});

if (environment.production) {
  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
  console.warn = () => {};
}

if (environment.production) {
  enableProdMode();
}

platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .catch((err) => console.error(err));
